import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, AlertTriangle } from 'lucide-react';
import {
  Template,
  extractTemplateVariables
} from '../../../types/models/whatsapp';
import templateService from '../../../services/whatsapp/templateService';

interface TemplatePickerProps {
  open: boolean;
  onClose: () => void;
  onSend: (
    template: Template,
    variables: Record<string, string>,
    header?: { type: 'image' | 'video' | 'document'; link: string }
  ) => Promise<void>;
}

/** Fills {{1}}, {{2}} … into the body so the preview reads like the real message. */
const renderPreview = (
  template: Template,
  variables: Record<string, string>
): string => {
  const body = template.components.find((c) => c.type === 'BODY');
  if (!body?.text) return '';
  return body.text.replace(/\{\{(\d+)\}\}/g, (match, index) => {
    const value = variables[index];
    return value?.trim() ? value : match;
  });
};

const TemplatePicker: React.FC<TemplatePickerProps> = ({
  open,
  onClose,
  onSend
}) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Template | null>(null);
  const [variables, setVariables] = useState<Record<string, string>>({});
  // A media header's file is chosen at SEND time — the approved template only
  // fixes the type (IMAGE/VIDEO/DOCUMENT), not which file goes in it.
  const [headerMediaUrl, setHeaderMediaUrl] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    templateService
      .listSendable()
      .then((result) => {
        if (!cancelled) setTemplates(result);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load templates');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  // Reset the form whenever the picker closes, so it never reopens half-filled.
  useEffect(() => {
    if (!open) {
      setSelected(null);
      setVariables({});
      setHeaderMediaUrl('');
      setSearch('');
      setSending(false);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter((t) => t.name.toLowerCase().includes(term));
  }, [templates, search]);

  const requiredVariables = selected ? extractTemplateVariables(selected) : [];
  const header = selected?.components.find((c) => c.type === 'HEADER');
  const footer = selected?.components.find((c) => c.type === 'FOOTER');
  const buttons =
    selected?.components.find((c) => c.type === 'BUTTONS')?.buttons || [];

  const needsHeaderMedia = Boolean(header && header.format !== 'TEXT');

  const allFilled =
    requiredVariables.every((key) => variables[key]?.trim()) &&
    (!needsHeaderMedia || /^https?:\/\//.test(headerMediaUrl.trim()));

  const handleSend = async () => {
    if (!selected || !allFilled || sending) return;
    setSending(true);
    setError(null);
    try {
      await onSend(
        selected,
        variables,
        needsHeaderMedia
          ? {
              type: header!.format!.toLowerCase() as
                | 'image'
                | 'video'
                | 'document',
              link: headerMediaUrl.trim()
            }
          : undefined
      );
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to send template');
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex h-[32rem] w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex w-1/2 flex-col border-r border-gray-200">
          <div className="flex items-center justify-between border-b border-gray-200 p-3">
            <h3 className="text-sm font-semibold text-gray-900">
              Choose a template
            </h3>
          </div>

          <div className="p-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search templates"
                className="w-full rounded-lg border-gray-300 pl-9 text-sm focus:border-primary-500 focus:ring-primary-500 text-gray-900"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-3 pb-3">
            {loading && (
              <p className="p-3 text-sm text-gray-500">Loading templates…</p>
            )}

            {!loading && filtered.length === 0 && (
              <div className="p-3 text-center">
                <p className="text-sm text-gray-500">No approved templates</p>
                <p className="mt-1 text-xs text-gray-400">
                  Templates must be approved by Meta before they can be sent.
                </p>
              </div>
            )}

            {filtered.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => {
                  setSelected(template);
                  setVariables({});
                  setHeaderMediaUrl('');
                }}
                className={`mb-1.5 w-full rounded-lg border p-2.5 text-left transition-colors ${
                  selected?.id === template.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <span className="block truncate text-sm font-medium text-gray-900">
                  {template.name}
                </span>
                <span className="mt-0.5 flex items-center gap-1.5">
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase text-gray-600">
                    {template.category}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {template.language}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex w-1/2 flex-col">
          <div className="flex items-center justify-between border-b border-gray-200 p-3">
            <h3 className="text-sm font-semibold text-gray-900">
              Preview
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-gray-400 hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {!selected && (
              <p className="text-sm text-gray-400">
                Select a template to preview it.
              </p>
            )}

            {selected && (
              <>
                {/* Full preview: header, body, footer and buttons. Showing
                    only the body made the picker disagree with what the
                    recipient actually receives. */}
                <div className="rounded-lg bg-wa-chatBg p-3">
                  <div
                    className="ml-auto max-w-[85%] rounded-2xl bg-wa-bubbleOut px-3 py-2 text-[15px] leading-snug shadow-sm"
                    style={{ color: '#111827' }}
                  >
                    {header?.format === 'TEXT' && header.text && (
                      <p className="mb-1 font-semibold">{header.text}</p>
                    )}

                    {header && header.format !== 'TEXT' && (
                      <div className="mb-1.5 flex h-24 items-center justify-center rounded bg-black/10">
                        {headerMediaUrl ? (
                          header.format === 'IMAGE' ? (
                            <img
                              src={headerMediaUrl}
                              alt="Header"
                              className="h-24 w-full rounded object-cover"
                            />
                          ) : (
                            <span className="text-xs" style={{ color: '#4b5563' }}>
                              {header.format} attached
                            </span>
                          )
                        ) : (
                          <span className="text-xs" style={{ color: '#6b7280' }}>
                            {header.format} header — add a link below
                          </span>
                        )}
                      </div>
                    )}

                    <p className="whitespace-pre-wrap">
                      {renderPreview(selected, variables)}
                    </p>

                    {footer?.text && (
                      <p className="mt-1.5 text-[13px]" style={{ color: '#6b7280' }}>
                        {footer.text}
                      </p>
                    )}
                  </div>

                  {buttons.length > 0 && (
                    <div className="ml-auto mt-1 max-w-[85%] space-y-1">
                      {buttons.map((button, index) => (
                        <div
                          key={`${button.type}-${index}`}
                          className="rounded-lg bg-wa-bubbleOut px-3 py-1.5 text-center text-[14px] font-medium shadow-sm"
                          style={{ color: '#2563eb' }}
                        >
                          {button.text}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Media header: Meta requires a link at send time — the
                    approved template only fixes the TYPE, not the file. */}
                {header && header.format !== 'TEXT' && (
                  <div className="mt-3">
                    <label
                      htmlFor="header-media"
                      className="block text-xs font-medium text-gray-700"
                    >
                      {header.format.charAt(0) + header.format.slice(1).toLowerCase()}{' '}
                      URL <span className="text-danger-600">*</span>
                    </label>
                    <input
                      id="header-media"
                      type="url"
                      value={headerMediaUrl}
                      onChange={(e) => setHeaderMediaUrl(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      className="mt-0.5 w-full rounded-lg border-gray-300 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                    />
                    <p className="mt-0.5 text-[11px] text-gray-500">
                      Must be a public HTTPS link Meta can fetch.
                    </p>
                  </div>
                )}

                {requiredVariables.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs font-medium text-gray-700">
                      Fill in variables
                    </p>
                    {requiredVariables.map((key) => (
                      <div key={key}>
                        <label
                          htmlFor={`var-${key}`}
                          className="block text-xs text-gray-500"
                        >
                          {`{{${key}}}`}
                        </label>
                        <input
                          id={`var-${key}`}
                          type="text"
                          value={variables[key] || ''}
                          onChange={(e) =>
                            setVariables((prev) => ({
                              ...prev,
                              [key]: e.target.value
                            }))
                          }
                          className="mt-0.5 w-full rounded-lg border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500 text-gray-900"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {error && (
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-danger-50 p-2.5 text-xs text-danger-600">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 p-3">
            <button
              type="button"
              onClick={handleSend}
              disabled={!selected || !allFilled || sending}
              className="w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {sending ? 'Sending…' : 'Send template'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePicker;
